import React, { useState, useEffect } from 'react';
import { Breadcrumb, Switch } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import ActivitySwitch from 'modules/layoutModule/components/ActivitySwitch/ActivitySwitch';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
  Select,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import {
  getAskExpertArticleById,
  saveAskExpertArticle,
  getRepresentatives,
} from '../requests/expertArticlesRequests';
import 'moment/locale/pl';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import TextEditor from 'modules/layoutModule/components/TextEditor/TextEditor';

const AskExpertsEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [isActivityDisabled, toggleActivityDisable] = useState(false);
  const [fileImage1, setFileImage1] = useState<string | null>(null);
  const [fileImage2, setFileImage2] = useState<string | null>(null);
  const [fileImage1Changed, changedFileImage1] = useState<boolean>(false);
  const [fileImage2Changed, changedFileImage2] = useState<boolean>(false);
  const [contentValue, setContentValue] = useState<string | undefined>('');
  const [representatives, setRepresentatives] = useState<any[]>([]);

  useEffect(() => {
    toggleLoading(true);

    getRepresentatives().then((response) => {
      setRepresentatives([
        { label: '', value: '' },
        ...response.map((item: { name: string; id: number }) => ({
          label: item.name,
          value: item.id,
        })),
      ]);
    });

    getAskExpertArticleById(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;
          setFields([
            {
              name: ['id'],
              value: data.id,
            },
            {
              name: ['name'],
              value: data.name,
            },
            {
              name: 'idRepresentatives',
              value: data.idRepresentatives,
            },
            {
              name: ['ask'],
              value: data.ask,
            },
            {
              name: ['fileImage1'],
              value: data.fileImage1,
            },
            {
              name: ['fileImage2'],
              value: data.fileImageASK,
            },
            {
              name: ['sortOrder'],
              value: data.sortOrder,
            },
            {
              name: ['categories'],
              value: data.categories,
            },
            {
              name: ['isActive'],
              value: data.isActive || false,
            },
          ]);
          form.setFieldsValue({
            fileImage1: data?.fileImage1,
            fileImage2: data?.fileImageASK,
          });
          setContentValue(data.description);

          setFileImage1(data?.fileImage1);
          setFileImage2(data?.fileImageASK);
          if (data.activitySwitchDisabled) {
            toggleActivityDisable(true);
          }
        } else {
          history.push('/ask-expert');
        }
      })
      .catch(() => {
        history.push('/ask-expert');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    saveAskExpertArticle(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void,
    coverMethodChanged: (value: boolean) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
    coverMethodChanged(true);
  };

  const onContentEdit = (e: any) => {
    setFields([
      {
        name: ['description'],
        value: e,
      },
    ]);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/ask-expert">Zapytaj eksperta</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/ask-expert/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="fileImage1"
                label="Avatar Porady ekspertów 543x547px"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={fileImage1}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage1',
                      setFileImage1,
                      changedFileImage1
                    )
                  }
                  isNew={fileImage1Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage1"
                  number={1}
                  hideTags
                  dontResize
                />
              </Form.Item>
              <Form.Item
                name="fileImage2"
                label="Avatar zapytaj eksperta 430x370px"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={fileImage2}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage2',
                      setFileImage2,
                      changedFileImage2
                    )
                  }
                  isNew={fileImage2Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage2"
                  number={2}
                  hideTags
                  dontResize
                />
              </Form.Item>
              <Form.Item
                name="name"
                label="Imię i nazwisko"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Opis">
                <TextEditor
                  initialValue={contentValue}
                  onChange={onContentEdit}
                  disableGallery
                />
              </Form.Item>
              <Form.Item name="categories" label="Kategoria">
                <Input />
              </Form.Item>

              <Form.Item name="idRepresentatives" label="Powiązany z PT">
                <Select
                  showSearch
                  placeholder="Wybierz jedną z opcji"
                  optionFilterProp="children"
                  options={representatives}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item
                name="sortOrder"
                label="Kolejność"
                rules={[
                  {
                    required: true,
                  },
                  {
                    validator: async (_, value) => {
                      if (value) {
                        if (
                          (value.length > 5 ||
                            !/^[0-9]*\,?\.?[0-9]*$/.test(value)) &&
                          value.length > 0
                        ) {
                          return Promise.reject(
                            new Error('Proszę podać poprawną wartość!')
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  style={{
                    width: '100px',
                  }}
                />
              </Form.Item>
              <ActivitySwitch
                isActivityDisabled={isActivityDisabled}
                customText="Twórca Porad ekspertów"
              />
              <Form.Item
                name="ask"
                label={'Widoczny na podstronie Zapytaj eksperta'}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/ask-expert">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/ask-expert">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default AskExpertsEdit;
